import { LayoutDashboard } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  const effectiveDate = 'January 1, 2026';

  return (
    <div className="min-h-screen" style={{ background: '#F7F6F2' }}>
      <header className="flex items-center justify-between px-8 py-5" style={{ borderBottom: '1px solid #E5E3DB', background: '#FFFFFF' }}>
        <Link to="/" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#D98A4A' }}>
            <LayoutDashboard size={16} className="text-white" />
          </div>
          <span className="font-heading text-lg font-semibold italic" style={{ color: '#1A1A1A' }}>AI Snugglemind</span>
        </Link>
        <div className="flex items-center gap-4 font-body text-sm">
          <Link to="/terms" className="hover:underline" style={{ color: '#4B5563' }}>Terms of Use</Link>
          <Link to="/subscription-agreement" className="hover:underline" style={{ color: '#4B5563' }}>Subscription Agreement</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="privacy-policy-page">
        <h1 className="font-heading text-4xl font-medium mb-2" style={{ color: '#1A1A1A' }}>Privacy Policy</h1>
        <p className="font-body text-sm mb-10" style={{ color: '#9CA3AF' }}>Effective Date: {effectiveDate}</p>

        <div className="space-y-8 font-body text-sm leading-relaxed" style={{ color: '#4B5563' }}>
          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>1. Introduction</h2>
            <p>Snugglemind Inc. ("Company," "we," "us," or "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the AI Snugglemind platform ("Service").</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>2. Information We Collect</h2>
            <h3 className="font-body font-semibold mb-2" style={{ color: '#1A1A1A' }}>2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li><strong>Account Information:</strong> Name, email address, and password when you create an account</li>
              <li><strong>Financial Data:</strong> Transaction records, vendor information, chart of accounts, and other bookkeeping data you upload or enter</li>
              <li><strong>Client Profiles:</strong> Company identity, accounting policies, and operational context for your business clients</li>
              <li><strong>Payment Information:</strong> Billing details processed through our third-party payment processor (Stripe)</li>
              <li><strong>Communications:</strong> Messages sent through the support chat, sales inquiries, and feedback</li>
            </ul>
            <h3 className="font-body font-semibold mb-2" style={{ color: '#1A1A1A' }}>2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Usage Data:</strong> Pages visited, features used, and interaction patterns</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
              <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and preferences</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To provide, maintain, and improve the Service</li>
              <li>To process transactions and manage your subscription</li>
              <li>To run AI-powered transaction analysis and generate bookkeeping insights</li>
              <li>To send you service-related notifications and updates</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations and enforce our Terms of Use</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>4. AI Data Processing</h2>
            <p>Our Service uses artificial intelligence to analyze your financial data. Specifically:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Transaction data is processed by our AI engine to provide classification suggestions</li>
              <li>Your data may be sent to third-party AI providers (e.g., OpenAI) for processing, subject to their privacy policies</li>
              <li>We do not use your financial data to train general-purpose AI models</li>
              <li>AI analysis results are stored in your account and are accessible only to you and authorized users</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>5. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Service Providers:</strong> Third-party vendors who assist in operating the Service (e.g., hosting, payment processing, AI analysis)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>6. Data Security</h2>
            <p>We implement industry-standard security measures to protect your information, including:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure authentication with hashed passwords and HTTP-only cookies</li>
              <li>Brute-force protection on login endpoints</li>
              <li>Regular security assessments and monitoring</li>
            </ul>
            <p className="mt-2">While we strive to protect your information, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>7. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide the Service. Financial data and audit trails are retained for the duration of your subscription plus a reasonable period for compliance purposes. You may request deletion of your account and associated data at any time by contacting us.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>8. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a machine-readable copy of your data</li>
              <li><strong>Objection:</strong> Object to certain processing of your information</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, please contact us at admin@snugglemind.com.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>9. Cookies</h2>
            <p>We use essential cookies for authentication and session management. These cookies are strictly necessary for the Service to function and cannot be disabled. We do not use advertising or tracking cookies.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>10. Children's Privacy</h2>
            <p>The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we learn that we have collected information from a child, we will take steps to delete it promptly.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on the Service with a new effective date. Your continued use of the Service after such changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>12. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
            <p className="mt-2"><strong style={{ color: '#1A1A1A' }}>Snugglemind Inc.</strong><br />Email: admin@snugglemind.com</p>
          </section>
        </div>

        <div className="mt-12 pt-6 flex items-center gap-4 font-body text-xs" style={{ borderTop: '1px solid #E5E3DB', color: '#9CA3AF' }}>
          <span>&copy; {new Date().getFullYear()} Snugglemind Inc. All rights reserved.</span>
          <Link to="/terms" className="hover:underline" style={{ color: '#D98A4A' }}>Terms of Use</Link>
          <Link to="/subscription-agreement" className="hover:underline" style={{ color: '#D98A4A' }}>Subscription Agreement</Link>
        </div>
      </div>
    </div>
  );
}
